// MIDAS V3 — Variant E status/conditions table: detection + hast annotation
// (MIDAS-ANSWER-FORMAT-1 round 5b, owner go 2026-10-04; Paper file "MIDAS
// answer format — before / after", board S10, frame "Variant E").
//
// WHAT IS DETECTED (BOT_CAPABILITIES.md §12, bot 99317a5 + 4934a2d):
//   the variant-C status table after round 5 — columns
//     mark | condition | address | symbol | live-pulse
//   with a judgement mark ✓ / ✕ / – / ? as the whole first cell of every row
//   and the live-pulse cell either empty, a <live-row/> (LIVEROW sentinel) or
//   the documented MIDAS_SRC_CHIPS=off degrade word ("naživo" / "live" /
//   "živě" / it "in tempo reale" / pl "na żywo"). EXACTLY five columns: the pre-round-5 four-column table
//   (STAV | MEANING | SYMBOL | TAG) is NOT this shape and keeps today's look.
//   Anything that misses a single rule renders as a plain markdown table —
//   the styling is opt-in per table, never a guess. The mark / pulse rules
//   look at BODY rows only, so a header row of wire tokens (c-6: `| STAV |
//   CONDITION | ADDRESS | SYMBOL | LIVE |`) never decides detection.
//
// r5c (bot revision c-6, MACHINEID_TODO.md 2026-10-04 "round 5 / r5b
// answers"): "?" splits in two. Without a pulse = could not be read →
// "neoverené" (grey row, label, counted). With a pulse = read but cannot be
// judged → keeps the "?" badge only. The bar's count is the bot's N.
//
// HOW IT FLOWS: a rehype plugin (markdownComponentsV3 →
// MARKDOWN_REHYPE_PLUGINS_V3) runs annotateStatusTables over the hast tree
// before react-markdown renders it. Matching nodes get a `data.midasStatus*`
// annotation (hast `data` never reaches the DOM); the table / thead / th / tr /
// td / p renderers read it back with the readers at the bottom. A node with
// no annotation renders exactly as before.
//
// Pure — no React, no DOM.
import {
  QUIET_LINE_MAX_CHARS,
  isRedRowsSufficeSentence,
  isUnverifiableSentence,
} from "./statusQuietLineGrammar";
// MIDAS-VERIFIED-LINE-1: the "verified" band paragraph directly ABOVE a
// detected status table (marker + position only — never wording).
import { isVerifiedLineParagraph } from "./verifiedLineGrammar";

export type StatusMark = "fail" | "ok" | "unknown" | "na";

export type StatusColumnRole = "mark" | "condition" | "address" | "symbol" | "pulse";

/** Column order of the round-5 status table (BOT_CAPABILITIES §12). */
export const STATUS_COLUMN_ROLES: StatusColumnRole[] = [
  "mark",
  "condition",
  "address",
  "symbol",
  "pulse",
];

/** The glyph each badge draws (aliases are normalised to these). */
export const STATUS_MARK_GLYPH: Record<StatusMark, string> = {
  fail: "✕",
  ok: "✓",
  unknown: "?",
  na: "–",
};

// The documented marks plus their common Unicode look-alikes (the marks are
// model-written under prompt variant C, not server-minted). Emoji forms
// (✅ ❌) are NOT accepted — they are outside the documented set.
const MARK_ALIASES: Record<string, StatusMark> = {
  "✓": "ok",
  "✔": "ok",
  "✕": "fail",
  "✖": "fail",
  "✗": "fail",
  "✘": "fail",
  "?": "unknown",
  "？": "unknown",
  "–": "na",
  "—": "na",
  "-": "na",
};

// Escaped (not raw) invisibles: VS15/VS16, ZWSP..ZWJ, WORD JOINER. Raw chars in a regex
// literal survive bundling unescaped and break when a page decodes the script as non-UTF-8.
const INVISIBLES_RE = /[\s\uFE0E\uFE0F\u200B-\u200D\u2060]/g;

/** A cell's text → its mark, or null when it is anything but ONE mark. */
export const parseStatusMark = (raw: string): StatusMark | null => {
  const key = (raw || "").replace(INVISIBLES_RE, "");
  return Object.prototype.hasOwnProperty.call(MARK_ALIASES, key)
    ? MARK_ALIASES[key]
    : null;
};

// MIDAS_SRC_CHIPS=off: the bot writes a plain word where <live-row/> would be.
// Bot-confirmed (r5b answer 3): sk/cs "naživo", en/de "live"; "živě" is never
// sent but stays accepted (harmless). USER-LANG-IT-PL-1 (bot 2026-10-31,
// `25815ce`): it "in tempo reale", pl "na żywo"; de stays "live".
const PULSE_DEGRADE_RE = /^(?:naživo|live|živě|in\s+tempo\s+reale|na\s+żywo)$/i;

// The row already says it is unverified — do not append the label twice.
// USER-LANG-IT-PL-1 (bot 2026-10-31, `25815ce`): labels it "Non verificato:",
// pl "Niezweryfikowane:", de "Nicht verifiziert:"; in sentences it "non è
// verificat…", pl "zweryfikowan…", de "nicht verifiziert". The Italian stem
// carries an Italian ending (o/a/i/e) so the English "verification" never
// suppresses the label.
const UNVERIFIED_MENTION_RE =
  /neoveren|neověřen|unverified|not\s+verified|verificat[oaie]\b|zweryfikowan|nicht\s+verifiziert/i;

// c-6 sends a fixed header row of wire tokens (sk/cs `| STAV | CONDITION |
// ADDRESS | SYMBOL | LIVE |`, en `| STATE | … |`). They name columns, not the
// question, so the bar shows no title for them (counts only). From c-7 the
// header row carries the real title + output (`| | <title> | <address> | | |`).
const GENERIC_HEADER_RE = /^(?:stav|state|condition|address|symbol|live)$/i;

const headerText = (raw: string): string => {
  const t = raw.trim();
  return GENERIC_HEADER_RE.test(t) ? "" : t;
};

// The quiet lines under the table live in their own module; re-exported so
// this stays the one import for the status-table grammar.
export { QUIET_LINE_MAX_CHARS, isRedRowsSufficeSentence, isUnverifiableSentence };

// ─── Annotation payloads ─────────────────────────────────────────────

export interface StatusTableInfo {
  /** Header cell above the condition column (Paper "PODMIENKY ŠTARTU");
   *  "" for c-6's generic wire tokens. */
  title: string;
  /** Header cell above the address column, only when it names an address
   *  (contains a digit, e.g. "Q 40.2") — a bare label like "ADRESA" is not
   *  an output and is left to the screen-reader header row. */
  output: string;
  counts: Record<StatusMark, number>;
  /** "?" rows WITHOUT a live pulse — the rows the bot could not read. Equals
   *  the bot's N in the quiet line. A "?" row WITH a pulse was read but cannot
   *  be judged (the logic uses the signal in both polarities): not counted. */
  unverified: number;
}

export interface StatusCellInfo {
  role: StatusColumnRole;
  /** Row mark; null on an all-empty row. */
  mark: StatusMark | null;
  /** The row's pulse cell carries a live read (<live-row/> or, with
   *  MIDAS_SRC_CHIPS=off, the degrade word). */
  live: boolean;
  /** Unverified ("?" without a pulse) row's symbol cell: append the
   *  "neoverené" label (after the symbol, or alone when the cell is empty).
   *  "none" everywhere else, including "?" rows that carry a pulse. */
  unverifiedSuffix: "none" | "after" | "alone";
  /** r6 `<cond-info text="…"/>`: the decoded explanation of a BODY condition
   *  cell (the first CONDINFO sentinel among the cell's direct text runs);
   *  null on every other cell, and whenever the caller passed no matcher.
   *  Never an input to detection. */
  condInfo: string | null;
}

/**
 * r6: how the grammar finds and decodes a CONDINFO sentinel without knowing
 * its layout (markdownComponentsV3 → COND_INFO_MATCHER). `re` matches ONE
 * sentinel (not global); `decode` takes that full match → the explanation, or
 * null when it does not validate.
 */
export interface CondInfoMatcher {
  re: RegExp;
  decode: (sentinel: string) => string | null;
}

/** Row annotation: a mark, or "blank" for an all-empty row. */
export type StatusRowMark = StatusMark | "blank";

interface HastNode {
  type: string;
  tagName?: string;
  value?: string;
  children?: HastNode[];
  data?: Record<string, unknown>;
}

const KEY_TABLE = "midasStatusTable";
const KEY_HEAD = "midasStatusHead";
const KEY_TH = "midasStatusTh";
const KEY_ROW = "midasStatusRow";
const KEY_ROW_UNVERIFIED = "midasStatusRowUnverified";
const KEY_CELL = "midasStatusCell";
const KEY_QUIET = "midasStatusQuiet";
const KEY_VERIFIED = "midasStatusVerified";
// MIDAS-VERIFIED-LINE-1 placement fix (owner 2026-10-11 "1b"): the table's
// link to that paragraph, so the band renders INSIDE its card (Paper).
const KEY_VERIFIED_LINE = "midasStatusVerifiedLine";

// Plain boolean (not a type guard): a guard would narrow the false branch of
// `isEl(child, "table")` to `never`.
const isEl = (n: HastNode | undefined, ...tags: string[]): boolean =>
  !!n && n.type === "element" && !!n.tagName && tags.includes(n.tagName);

// `raw` = inline HTML react-markdown has not yet turned into text (e.g. a
// malformed chip left verbatim) — it counts as cell text.
const textOf = (n: HastNode): string =>
  n.type === "text" || n.type === "raw"
    ? n.value || ""
    : (n.children || []).map(textOf).join("");

const rowsOf = (section: HastNode): HastNode[] =>
  (section.children || []).filter((c) => isEl(c, "tr"));

const cellsOf = (tr: HastNode): HastNode[] =>
  (tr.children || []).filter((c) => isEl(c, "td", "th"));

const setData = (n: HastNode, key: string, value: unknown) => {
  n.data = { ...(n.data || {}), [key]: value };
};

interface StatusRowAnalysis {
  tr: HastNode;
  cells: HastNode[];
  mark: StatusMark | null;
  texts: string[];
  /** The pulse cell carries a live read (sentinel or degrade word). */
  live: boolean;
  /** r6: decoded condition-cell explanation (body rows; null otherwise). */
  condInfo?: string | null;
  /** r6: `texts` with every CONDINFO sentinel removed — what the "already
   *  says unverified" test reads, so an explanation can never suppress the
   *  visible label (SEC-REQ-CI-6). Equals `texts` without a matcher. */
  plainTexts?: string[];
}

// r6: the first CONDINFO sentinel among a condition cell's DIRECT children, in
// order. Text and raw runs are searched; an element child (e.g. `**bold**`)
// whose subtree holds a sentinel FIRST ends the search with null, because the
// cell renderer strips only from its direct text runs — the explanation and
// the stripped sentinel must be the same occurrence (anything else stays
// visible plain text).
const firstCondInfo = (cell: HastNode, ci: CondInfoMatcher): string | null => {
  for (const child of cell.children || []) {
    const isRun = child.type === "text" || child.type === "raw";
    const m = (isRun ? child.value || "" : textOf(child)).match(ci.re);
    if (m) return isRun ? ci.decode(m[0]) : null;
  }
  return null;
};

export interface StatusTableAnalysis {
  info: StatusTableInfo;
  thead: HastNode | undefined;
  headCells: HastNode[];
  rows: StatusRowAnalysis[];
}

/**
 * Decide whether a hast `table` is the round-5 status table. `liveRowSentinel`
 * is the full LIVEROW sentinel string (markdownComponentsV3 owns it).
 * Returns null — "render as a plain table" — on the first rule it misses.
 * r6: the optional `condInfo` matcher only ATTACHES each body row's condition
 * explanation AFTER detection has decided; it never takes part in it (bot
 * round-6 ask 4: "an element in the second cell must not change it").
 */
export const analyzeStatusTable = (
  table: unknown,
  liveRowSentinel: string,
  condInfo?: CondInfoMatcher
): StatusTableAnalysis | null => {
  const t = table as HastNode | undefined;
  if (!t || !isEl(t, "table")) return null;
  const sections = (t.children || []).filter((c) => isEl(c, "thead", "tbody"));
  const thead = sections.find((s) => isEl(s, "thead"));
  const headRow = thead ? rowsOf(thead)[0] : undefined;
  const headCells = headRow ? cellsOf(headRow) : [];
  if (headCells.length !== STATUS_COLUMN_ROLES.length) return null;

  const bodyRows: HastNode[] = [];
  sections
    .filter((s) => isEl(s, "tbody"))
    .forEach((s) => rowsOf(s).forEach((tr) => bodyRows.push(tr)));
  if (bodyRows.length === 0) return null;

  const counts: Record<StatusMark, number> = { fail: 0, ok: 0, unknown: 0, na: 0 };
  let unverified = 0;
  const rows: StatusRowAnalysis[] = [];
  for (const tr of bodyRows) {
    const cells = cellsOf(tr);
    if (cells.length !== STATUS_COLUMN_ROLES.length) return null;
    const texts = cells.map(textOf);
    // An all-empty row is not a data row (and is what a stream's trailing
    // `|` parses to) — it neither qualifies nor disqualifies the table.
    if (texts.every((x) => x.trim() === "")) {
      rows.push({ tr, cells, mark: null, texts, live: false });
      continue;
    }
    const mark = parseStatusMark(texts[0]);
    if (!mark) return null;
    const rawPulse = texts[4].trim();
    const pulse = texts[4].split(liveRowSentinel).join("").trim();
    if (pulse !== "" && !PULSE_DEGRADE_RE.test(pulse)) return null;
    const live = rawPulse !== "";
    counts[mark] += 1;
    if (mark === "unknown" && !live) unverified += 1;
    rows.push({ tr, cells, mark, texts, live });
  }
  // A column of dashes alone is not a status judgement.
  if (counts.fail + counts.ok + counts.unknown === 0) return null;

  // r6 — detection is done; now (and only now) attach the explanations.
  const condInfoAll = condInfo ? new RegExp(condInfo.re.source, "g") : null;
  rows.forEach((r) => {
    r.condInfo = condInfo && r.mark ? firstCondInfo(r.cells[1], condInfo) : null;
    r.plainTexts = condInfoAll
      ? r.texts.map((x) => x.replace(condInfoAll, " "))
      : r.texts;
  });

  const output = headerText(textOf(headCells[2]));
  return {
    info: {
      title: headerText(textOf(headCells[1])),
      output: /\d/.test(output) ? output : "",
      counts,
      unverified,
    },
    thead,
    headCells,
    rows,
  };
};

// Next element sibling, skipping whitespace-only text; any other node between
// (e.g. real text) means the table has no directly-following element.
const nextElementSibling = (siblings: HastNode[], index: number): HastNode | null => {
  for (let i = index + 1; i < siblings.length; i += 1) {
    const s = siblings[i];
    if (s.type === "text" && (s.value || "").trim() === "") continue;
    return s.type === "element" ? s : null;
  }
  return null;
};

// MIDAS-VERIFIED-LINE-1 — mirror of nextElementSibling: the previous element
// sibling, skipping whitespace-only text; any other node between (e.g. real
// text) means the table has no directly-preceding element.
const prevElementSibling = (siblings: HastNode[], index: number): HastNode | null => {
  for (let i = index - 1; i >= 0; i -= 1) {
    const s = siblings[i];
    if (s.type === "text" && (s.value || "").trim() === "") continue;
    return s.type === "element" ? s : null;
  }
  return null;
};

const annotateTable = (a: StatusTableAnalysis, table: HastNode) => {
  setData(table, KEY_TABLE, a.info);
  if (a.thead) setData(a.thead, KEY_HEAD, true);
  a.headCells.forEach((c) => setData(c, KEY_TH, true));
  a.rows.forEach((r) => {
    setData(r.tr, KEY_ROW, r.mark || "blank");
    // Only a "?" row with NO live read is unverified ("?" + pulse = read,
    // cannot be judged: keeps its badge, never says "neoverené").
    const isUnverified = r.mark === "unknown" && !r.live;
    if (isUnverified) setData(r.tr, KEY_ROW_UNVERIFIED, true);
    // r6 (SEC-REQ-CI-6): read the row WITHOUT its CONDINFO sentinels — the
    // URI-encoded body keeps ASCII letters, so an explanation saying
    // "unverified" / "neoverené" would otherwise suppress the visible label.
    const saysUnverified = UNVERIFIED_MENTION_RE.test((r.plainTexts || r.texts).join(" "));
    r.cells.forEach((c, col) => {
      const role = STATUS_COLUMN_ROLES[col];
      let unverifiedSuffix: StatusCellInfo["unverifiedSuffix"] = "none";
      if (role === "symbol" && isUnverified && !saysUnverified) {
        unverifiedSuffix = r.texts[col].trim() === "" ? "alone" : "after";
      }
      const condInfo = role === "condition" ? r.condInfo || null : null;
      const info: StatusCellInfo = {
        role,
        mark: r.mark,
        live: r.live,
        unverifiedSuffix,
        condInfo,
      };
      setData(c, KEY_CELL, info);
    });
  });
};

const isParagraphWith = (n: HastNode | null, test: (text: string) => boolean): boolean =>
  !!n && isEl(n, "p") && test(textOf(n));

/**
 * Walk a hast tree and annotate every round-5 status table, plus the quiet
 * "N … nedá(jú) overiť" paragraph directly after one that has unverified rows
 * (the bot sends it only when its N > 0), plus the optional fixed "Stačia
 * červené riadky — …" paragraph directly after that quiet line.
 *
 * MIDAS-VERIFIED-LINE-1: with the optional `verifiedLineSentinel` (the full
 * VERIFIEDLINE sentinel string — markdownComponentsV3 owns it), also marks the
 * paragraph DIRECTLY ABOVE a detected status table whose text starts with that
 * sentinel (isVerifiedLineParagraph), and links that paragraph from the table
 * (the band renders inside the table's card). The marker lives outside the
 * table and takes no part in detection — analyzeStatusTable never sees it.
 */
export const annotateStatusTables = (
  tree: unknown,
  liveRowSentinel: string,
  condInfo?: CondInfoMatcher,
  verifiedLineSentinel?: string
): void => {
  const walk = (node: HastNode) => {
    const kids = node.children || [];
    kids.forEach((child, i) => {
      if (isEl(child, "table")) {
        const a = analyzeStatusTable(child, liveRowSentinel, condInfo);
        if (!a) return;
        annotateTable(a, child);
        if (verifiedLineSentinel) {
          const above = prevElementSibling(kids, i);
          if (above && isParagraphWith(above, (t) => isVerifiedLineParagraph(t, verifiedLineSentinel))) {
            setData(above, KEY_VERIFIED, true);
            // Same gate result, recorded on the table too: a reference to the
            // paragraph node (never a copy of its text — SEC-REQ-VL-2).
            setData(child, KEY_VERIFIED_LINE, above);
          }
        }
        if (a.info.unverified > 0) {
          const quiet = nextElementSibling(kids, i);
          if (quiet && isParagraphWith(quiet, isUnverifiableSentence)) {
            setData(quiet, KEY_QUIET, true);
            const after = nextElementSibling(kids, kids.indexOf(quiet));
            if (after && isParagraphWith(after, isRedRowsSufficeSentence)) {
              setData(after, KEY_QUIET, true);
            }
          }
        }
        return; // tables do not nest in GFM
      }
      if (child.children) walk(child);
    });
  };
  const root = tree as HastNode | undefined;
  if (root && Array.isArray(root.children)) walk(root);
};

// ─── Readers (renderers receive the hast node as `node`) ─────────────

const dataOf = (node: unknown): Record<string, unknown> | undefined => {
  if (!node || typeof node !== "object") return undefined;
  return (node as HastNode).data;
};

export const statusTableInfoOf = (node: unknown): StatusTableInfo | null =>
  (dataOf(node)?.[KEY_TABLE] as StatusTableInfo | undefined) || null;

export const isStatusHead = (node: unknown): boolean => dataOf(node)?.[KEY_HEAD] === true;

export const isStatusHeaderCell = (node: unknown): boolean =>
  dataOf(node)?.[KEY_TH] === true;

export const statusRowMarkOf = (node: unknown): StatusRowMark | null =>
  (dataOf(node)?.[KEY_ROW] as StatusRowMark | undefined) || null;

/** A "?" row with no live read — the grey "neoverené" row. */
export const isStatusRowUnverified = (node: unknown): boolean =>
  dataOf(node)?.[KEY_ROW_UNVERIFIED] === true;

export const statusCellOf = (node: unknown): StatusCellInfo | null =>
  (dataOf(node)?.[KEY_CELL] as StatusCellInfo | undefined) || null;

export const isStatusQuietLine = (node: unknown): boolean =>
  dataOf(node)?.[KEY_QUIET] === true;

/** MIDAS-VERIFIED-LINE-1: the `<verified-line/>` paragraph directly above a
 *  detected status table. Its own `p` renders NOTHING — the band is drawn
 *  inside that table's card (statusVerifiedLineTextOf below). */
export const isStatusVerifiedLine = (node: unknown): boolean =>
  dataOf(node)?.[KEY_VERIFIED] === true;

/** MIDAS-VERIFIED-LINE-1 (placement fix): for a status TABLE node, the text
 *  of its verified-line paragraph (sentinels included, marker still leading),
 *  read from the hast at render time; null when it has none. Text only —
 *  inline markup flattens to its text (the bot sends a plain sentence). */
export const statusVerifiedLineTextOf = (node: unknown): string | null => {
  const para = dataOf(node)?.[KEY_VERIFIED_LINE] as HastNode | undefined;
  return para && isEl(para, "p") ? textOf(para) : null;
};
